import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert } from './ui/alert';
import { UserPlus, Edit, Trash2, UserCheck, UserX, RefreshCw, Copy, CheckCircle, Clock, Link as LinkIcon, MessageSquare } from 'lucide-react';
import { userManagementService } from '../services/user-management-service';
import { User, UserInvitation } from '../types/user';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../lib/supabase';

interface UsersManagementProps {
  tenantId: string;
  currentUser: User;
}

interface UserWithStats extends User {
  messageCount?: number;
  lastActivity?: string;
}

export function UsersManagement({ tenantId, currentUser }: UsersManagementProps) {
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    managers: 0,
    regularUsers: 0,
    recentUsers: 0
  });

  // Estados para modais
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteRole, setInviteRole] = useState<'user' | 'manager'>('user');
  const [inviteMode, setInviteMode] = useState<'direct' | 'link'>('link');
  const [generatedLink, setGeneratedLink] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'user' as 'user' | 'manager',
    status: 'active' as 'active' | 'inactive'
  });

  const toast = useToast();

  // Carregar dados
  const loadData = async () => {
    try {
      setIsLoading(true);
      const usersData = await userManagementService.getTenantUsers(tenantId);
      const statsData = await userManagementService.getTenantStats(tenantId);

      // Buscar contagem de mensagens para cada usuário
      const usersWithStats: UserWithStats[] = await Promise.all(
        usersData.map(async (user) => {
          try {
            // Buscar contagem de mensagens do usuário
            const { count: messageCount } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', user.id);

            // Buscar última atividade
            const { data: lastMessage } = await supabase
              .from('messages')
              .select('created_at')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .single();

            return {
              ...user,
              messageCount: messageCount || 0,
              lastActivity: lastMessage?.created_at || user.lastLogin
            };
          } catch (error) {
            console.error(`Erro ao buscar stats do usuário ${user.id}:`, error);
            return {
              ...user,
              messageCount: 0,
              lastActivity: user.lastLogin
            };
          }
        })
      );

      setUsers(usersWithStats);
      setStats(statsData);
      
      // Invitations podem ser vazias por enquanto
      setInvitations([]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos usuários');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenantId]);

  // Criar usuário ou link
  const handleCreateInvitation = async () => {
    try {
      if (inviteMode === 'direct') {
        // Criar usuário diretamente
        if (!inviteName.trim() || !inviteEmail.trim() || !invitePassword.trim()) {
          toast.error('Por favor, preencha todos os campos');
          return;
        }

        await userManagementService.createUserDirectly(
          tenantId,
          inviteEmail.trim(),
          inviteName.trim(),
          invitePassword.trim(),
          inviteRole
        );
        
        toast.success('Usuário criado com sucesso!');
        setShowInviteModal(false);
        setInviteName('');
        setInviteEmail('');
        setInvitePassword('');
        setInviteRole('user');
        setGeneratedLink('');
        loadData();
      } else {
        // Gerar link de cadastro
        if (!inviteName.trim() || !inviteEmail.trim()) {
          toast.error('Por favor, preencha nome e email');
          return;
        }

        const result = await userManagementService.generateSignupLink(
          tenantId,
          inviteRole
        );
        
        if (result.success && result.link) {
          setGeneratedLink(result.link);
          toast.success('Link gerado com sucesso!');
        } else {
          toast.error(result.error || 'Erro ao gerar link');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar convite');
    }
  };

  // Copiar link gerado
  const handleCopyGeneratedLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success('Link copiado para a área de transferência!');
  };

  // Fechar modal e limpar
  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteName('');
    setInviteEmail('');
    setInvitePassword('');
    setInviteRole('user');
    setGeneratedLink('');
    setInviteMode('link');
  };

  // Editar usuário
  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      await userManagementService.updateUserRole(selectedUser.id, editForm.role);

      toast.success('Usuário atualizado com sucesso!');
      setShowEditModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar usuário');
    }
  };

  // Deletar usuário
  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Tem certeza que deseja remover o usuário ${user.name} do tenant?`)) return;

    try {
      await userManagementService.deleteUser(user.id);
      toast.success('Usuário removido com sucesso!');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao remover usuário');
    }
  };

  // Abrir modal de edição
  const openEditModal = (user: UserWithStats) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge className="bg-green-100 text-green-800">
        <UserCheck className="w-3 h-3 mr-1" />
        Ativo
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800">
        <UserX className="w-3 h-3 mr-1" />
        Inativo
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Usuários</h2>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowInviteModal(true)}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Adicionar Usuário
        </Button>
      </div>

      {/* Tabela de Usuários */}
      {isLoading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-4 text-gray-500">Carregando usuários...</p>
          </CardContent>
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <UserX className="w-12 h-12 mx-auto text-gray-400" />
            <p className="mt-4 text-gray-500">Nenhum usuário cadastrado</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Interações</TableHead>
                  <TableHead>Último Acesso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openEditModal(user)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'manager' || user.role === 'owner' ? 'default' : 'secondary'}>
                        {user.role === 'owner' ? 'Owner' : user.role === 'manager' ? 'Gestor' : 'Usuário'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(user.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{user.messageCount || 0}</span>
                        <span className="text-sm text-gray-500">mensagens</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {user.lastActivity ? new Date(user.lastActivity).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Nunca'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                        {user.role !== 'owner' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(user)}
                            title="Editar usuário"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        )}
                        {user.id !== currentUser.id && user.role !== 'owner' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-700 hover:border-red-300"
                            title="Excluir usuário"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}


      {/* Modal de Edição */}
      {showEditModal && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Usuário</DialogTitle>
              <DialogDescription>
                Gerencie as informações e permissões de {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Informações do Usuário */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Nome</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedUser?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Email</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedUser?.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Interações</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {(selectedUser as UserWithStats)?.messageCount || 0} mensagens
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Último acesso</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {(selectedUser as UserWithStats)?.lastActivity 
                      ? new Date((selectedUser as UserWithStats).lastActivity!).toLocaleDateString('pt-BR')
                      : 'Nunca'}
                  </span>
                </div>
              </div>

              {/* Edição de Função */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-900">Função na Empresa</Label>
                <Select 
                  value={editForm.role} 
                  onValueChange={(value: 'user' | 'manager') => setEditForm({ ...editForm, role: value })}
                  disabled={selectedUser?.role === 'owner'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Gestor</SelectItem>
                    <SelectItem value="user">Usuário</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {selectedUser?.role === 'owner' 
                    ? 'Owner tem controle total da empresa e não pode ser alterado'
                    : editForm.role === 'manager' 
                      ? 'Gestores podem configurar a empresa e gerenciar usuários' 
                      : 'Usuários regulares têm acesso apenas ao chat com IAs'}
                </p>
              </div>

              {/* Ações Perigosas */}
              {selectedUser?.id !== currentUser.id && (
                <div className="border-t pt-4 space-y-2">
                  <Label className="text-sm font-medium text-gray-900">Ações</Label>
                  <p className="text-xs text-gray-500">
                    {selectedUser?.role === 'owner' 
                      ? 'O Owner não pode ser removido da empresa.'
                      : 'Remova este usuário da empresa. Esta ação não pode ser desfeita.'}
                  </p>
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:border-red-300 hover:bg-red-50"
                    onClick={() => {
                      setShowEditModal(false);
                      handleDeleteUser(selectedUser);
                    }}
                    disabled={selectedUser?.role === 'owner'}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir Usuário
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleEditUser} 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={selectedUser?.role === 'owner'}
              >
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Adicionar Usuário */}
      {showInviteModal && (
        <Dialog open={showInviteModal} onOpenChange={handleCloseInviteModal}>
          <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Usuário</DialogTitle>
            <DialogDescription>
              Escolha como deseja adicionar o usuário à sua empresa.
            </DialogDescription>
          </DialogHeader>
          
          {!generatedLink ? (
            <>
              {/* Abas de modo */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    inviteMode === 'link'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setInviteMode('link')}
                >
                  <LinkIcon className="w-4 h-4 inline mr-1" />
                  Gerar Link
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    inviteMode === 'direct'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setInviteMode('direct')}
                >
                  <UserPlus className="w-4 h-4 inline mr-1" />
                  Criar Direto
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nome</label>
                  <Input
                    placeholder="João Silva"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                {inviteMode === 'direct' && (
                  <div>
                    <label className="text-sm font-medium">Senha</label>
                    <Input
                      type="password"
                      placeholder="Senha para o usuário"
                      value={invitePassword}
                      onChange={(e) => setInvitePassword(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">Função</label>
                  <Select value={inviteRole} onValueChange={(value: 'user' | 'manager') => setInviteRole(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuário</SelectItem>
                      <SelectItem value="manager">Gestor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseInviteModal}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateInvitation}>
                  {inviteMode === 'link' ? (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Gerar Link
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Criar Usuário
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Mostrar link gerado */}
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <div className="ml-2">
                    <p className="font-medium text-green-900">Link gerado com sucesso!</p>
                    <p className="text-sm text-green-700 mt-1">
                      Compartilhe este link com {inviteName} para que ele possa se cadastrar.
                    </p>
                  </div>
                </Alert>
                
                <div>
                  <label className="text-sm font-medium">Link de Cadastro</label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={generatedLink}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button onClick={handleCopyGeneratedLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseInviteModal}>
                  Fechar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}